import type { GetScorerResponse } from '@mastra/client-js';
import { useQuery } from '@tanstack/react-query';
import { isPlainObject } from 'lodash';

import { captureException } from '../../logger/Logger';
import { useMastraClient } from '../providers/MastraClient';

const readScorerField = (
    entry: GetScorerResponse,
    field: 'id' | 'name',
): string | null => {
    const scorer = entry.scorer;
    if (!isPlainObject(scorer)) {
        return null;
    }

    // listScorers HTTP response serializes as scorer.config.{id,name}.
    const config = scorer.config;
    if (isPlainObject(config)) {
        const configValue = field === 'id' ? config.id : config.name;
        if (typeof configValue === 'string' && configValue !== '') {
            return configValue;
        }
    }

    const value = field === 'id' ? scorer.id : scorer.name;
    return typeof value === 'string' && value !== '' ? value : null;
};

const toScorerDisplayNameMap = (
    scorers: Record<string, GetScorerResponse>,
): Record<string, string> => {
    const displayNames: Record<string, string> = {};

    for (const [key, entry] of Object.entries(scorers)) {
        const scorerId = readScorerField(entry, 'id') ?? key;
        displayNames[scorerId] = readScorerField(entry, 'name') ?? scorerId;
    }

    return displayNames;
};

export const getScorerDisplayName = (
    scorerDisplayNames: Record<string, string> | undefined,
    scorerId: string,
): string => scorerDisplayNames?.[scorerId] ?? scorerId;

export const useListScorers = () => {
    const mastraClient = useMastraClient();

    return useQuery({
        queryKey: ['mastraScorers'],
        queryFn: async (): Promise<Record<string, string>> => {
            try {
                const scorers = await mastraClient.listScorers();
                return toScorerDisplayNameMap(scorers);
            } catch (err) {
                captureException(err as Error);
                throw err;
            }
        },
        staleTime: 5 * 60 * 1000,
    });
};
